import { toggleElementFullscreen } from "@workspace/blocks/splat-viewer/fullscreen";
import { Button } from "@workspace/blocks/ui";
import { AlertCircle, Download, Maximize, Minimize, RotateCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { useTranslation } from "../i18n";
import { ViewerLoadingState } from "./viewer/ViewerLoadingState";

interface HtmlViewerProps {
	src: string;
	title?: string;
	className?: string;
	children?: React.ReactNode;
	onError?: (error: Error) => void;
	onLoad?: () => void;
	previewMode?: boolean;
}
function HtmlControls({ className, children }: { className?: string; children: React.ReactNode }) {
	return (
		<div
			className={cn(
				"absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-xl border border-border bg-card/95 p-2 shadow-sm backdrop-blur",
				className,
			)}
		>
			{children}
		</div>
	);
}
function FullScreenButton({ containerRef }: { containerRef: React.RefObject<HTMLDivElement | null> }) {
	const { t } = useTranslation();
	const [fullscreen, setFullscreen] = useState(false);
	const [failed, setFailed] = useState(false);
	useEffect(() => {
		const update = () => setFullscreen(document.fullscreenElement === containerRef.current);
		document.addEventListener("fullscreenchange", update);
		return () => document.removeEventListener("fullscreenchange", update);
	}, [containerRef]);
	return (
		<>
			<Button
				variant="ghost"
				aria-label={fullscreen ? t("viewer.exitFullscreen") : t("viewer.fullscreen")}
				onClick={async () => {
					if (!containerRef.current) return;
					const entered = await toggleElementFullscreen(containerRef.current);
					setFailed(!fullscreen && !entered);
				}}
			>
				{fullscreen ? <Minimize /> : <Maximize />}
				{t("viewer.fullscreen")}
			</Button>
			{failed && (
				<output className="absolute bottom-full mb-3 w-64 rounded-lg border border-border bg-card p-3 text-sm text-foreground shadow-sm">
					{t("viewer.fullscreenUnavailable")}
				</output>
			)}
		</>
	);
}
function DownloadButton({ src }: { src: string }) {
	const { t } = useTranslation();
	return (
		<Button asChild variant="ghost">
			<a href={src} download target="_blank" rel="noreferrer">
				<Download />
				{t("viewer.download")}
			</a>
		</Button>
	);
}
function ReloadButton({ onReload }: { onReload: () => void }) {
	const { t } = useTranslation();
	return (
		<Button variant="ghost" onClick={onReload}>
			<RotateCw />
			{t("viewer.refresh")}
		</Button>
	);
}
export function HtmlViewer({ src, title, className, children, onError, onLoad, previewMode = false }: HtmlViewerProps) {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);
	const [attempt, setAttempt] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		setLoading(true);
		setFailed(false);
	}, [src]);
	const reload = useCallback(() => {
		setLoading(true);
		setFailed(false);
		setAttempt(value => value + 1);
	}, []);
	return (
		<div ref={containerRef} className={cn("relative h-full w-full overflow-hidden bg-muted", className)}>
			<iframe
				key={`${src}:${attempt}`}
				src={src}
				title={title || "HTML content"}
				className="absolute inset-0 h-full w-full border-0 bg-card outline-none"
				allow="camera; microphone; gyroscope; accelerometer; xr-spatial-tracking; fullscreen; autoplay; encrypted-media"
				sandbox="allow-scripts allow-forms allow-popups allow-presentation allow-downloads allow-modals allow-orientation-lock"
				onLoad={() => {
					setLoading(false);
					onLoad?.();
				}}
				onError={() => {
					setLoading(false);
					setFailed(true);
					onError?.(new Error(t("viewer.htmlError")));
				}}
			/>
			{loading && <ViewerLoadingState />}
			{failed && (
				<div className="absolute inset-0 z-20 flex items-center justify-center bg-card p-6">
					<div role="alert" className="space-y-4 text-center">
						<AlertCircle className="mx-auto size-8 text-destructive" />
						<p>{t("viewer.htmlError")}</p>
						<ReloadButton onReload={reload} />
					</div>
				</div>
			)}
			{!previewMode &&
				(children || (
					<HtmlControls>
						<FullScreenButton containerRef={containerRef} />
						<ReloadButton onReload={reload} />
					</HtmlControls>
				))}
		</div>
	);
}
HtmlViewer.Controls = HtmlControls;
HtmlViewer.FullScreenButton = FullScreenButton;
HtmlViewer.DownloadButton = DownloadButton;
HtmlViewer.ReloadButton = ReloadButton;
